import { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext(null);

const T = {
  en: {
    nav: {
      whoWeAre: "Who We Are",
      services: "Services",
      advantage: "Advantage",
      reviews: "Reviews",
      netSheet: "Seller Net",
      blog: "Blog",
      team: "Team",
      calculator: "Calculator",
      contact: "Contact",
    },
    hero: {
      overline: "Opeam Group — Real Estate & Mortgage Brokerage",
      line1: "Real estate.",
      line2: "Mortgage.",
      line3: "Under one roof.",
      subtext:
        "One team handling your transaction and your financing — buying, selling, or refinancing without the friction between realtor and loan officer.",
      cta: "Start a conversation",
      catch1: "One Roof.",
      catch2: "Endless Options.",
      espanol: "Hablamos Español",
      scrollLabel: "Scroll to content",
      alt: "Desert home beneath Arizona mountains at golden hour",
    },
    marquee: {
      items: [
        "Real Estate Sales",
        "Mortgage Brokerage",
        "50+ Lending Partners",
        "One Trusted Team",
        "Faster Closings",
      ],
    },
    ch1: {
      eyebrow: "Chapter 01 — Who We Are",
      h2a: "A full-service brokerage,",
      h2b: "under a single umbrella.",
      body: "We are a full-service real estate and mortgage brokerage team. By bridging real estate transactions and mortgage financing, we provide a streamlined, end-to-end experience for clients buying, selling, or refinancing their homes.",
      caption: "Fig. 01 — Arizona sky, golden hour",
      alt: "Arizona sky — sunlight through a saguaro cactus at golden hour",
    },
    ch2: {
      eyebrow: "Chapter 02 — Core Services",
      card1t: "Real Estate Sales & Representation",
      card1b: "Comprehensive seller representation designed to maximize home equity, streamline listing procedures, and execute smooth sales transactions.",
      card2t: "Mortgage Brokerage Services",
      card2b: "Direct client access to competitive wholesale lending products, customized loan options, and tailored financing solutions for buyers and existing homeowners.",
    },
    ch3: {
      eyebrow: "Chapter 03 — The Advantage",
      label: "wholesale banks & institutional lenders",
      body: "As an independent mortgage broker, we maintain active relationships with more than fifty wholesale banks and institutional lenders. Rather than offering rigid, single-institution loan products, we shop the entire market to secure optimal interest rates, flexible loan terms, and personalized financing structures aligned with your specific financial profile.",
    },
    ch4: {
      eyebrow: "Chapter 04 — Value Proposition",
      h2a: "No friction between",
      h2b: "realtor and loan officer.",
      row1t: "Clearer communication",
      row1b: "One conversation, one team — nothing lost between your agent and your lender.",
      row2t: "Faster closing timelines",
      row2b: "Transaction and financing move in parallel, not in sequence.",
      row3t: "A single trusted team",
      row3b: "Every phase of your home buying or selling journey, managed under one roof.",
    },
    ch5: {
      eyebrow: "Chapter 06 — Your Team",
      h2a: "The people behind",
      h2b: "the keys.",
      omarRole: "Mortgage & Real Estate",
      paulaRole: "Real Estate",
    },
    testi: {
      eyebrow: "Chapter 05 — Testimonials",
      h2a: "Kind words from",
      h2b: "the Valley.",
      quotes: [
        {
          text: "Omar found us a rate our own bank couldn't touch, and Paula had us under contract in nine days. One team really does change everything.",
          name: "Sarah & Daniel M.",
          detail: "First-time buyers — Mesa",
        },
        {
          text: "Selling and buying at the same time felt impossible until OPEAM handled both sides. Clear answers, fast closing, zero drama.",
          name: "Rebecca T.",
          detail: "Seller & buyer — Gilbert",
        },
        {
          text: "Refinancing in Spanish, at my kitchen table, with no pressure. They explained every number until I was comfortable signing.",
          name: "José L.",
          detail: "Refinance — Phoenix",
        },
      ],
    },
    net: {
      eyebrow: "Chapter 08 — Seller Net Sheet",
      h2a: "Know your",
      h2b: "net proceeds.",
      intro:
        "Selling? Slide in your numbers — sale price, what you still owe, the commission you choose to pay, and estimated costs — and see what you could walk away with.",
      price: "Sold price",
      owe: "Mortgage payoff (what you owe)",
      commission: "Realtor commission",
      closing: "Closing costs",
      title: "Title fees",
      repairs: "Repairs",
      resultTitle: "Estimated net to you",
      disclaimer:
        "Estimate only — actual payoff, commissions, taxes, and fees vary. HOA and prorations not included.",
      rowPrice: "Sold price",
      rowOwe: "Mortgage payoff",
      rowComm: "Realtor commission",
      rowClosing: "Closing costs",
      rowTitle: "Title fees",
      rowRepairs: "Repairs",
      cta: "Get your exact net sheet",
      prefill:
        "I used your seller net sheet: sold price {price}, payoff {owe}, {comm}% commission, closing costs {closing}, title fees {title}, repairs {repairs} — estimated net {net}. I'd like an exact net sheet for my home.",
    },
    calc: {
      eyebrow: "Chapter 07 — Rate Calculator",
      h2a: "Know your",
      h2b: "monthly.",
      intro:
        "Slide the numbers to your situation and see the full monthly picture — principal, interest, taxes, and insurance. When you're ready, we'll shop 50+ lenders for your exact rate.",
      price: "Home price",
      down: "Down payment",
      rate: "Interest rate",
      tax: "Property tax (yearly)",
      insurance: "Home insurance (yearly)",
      term: "Loan term",
      yr: "yr",
      resultTitle: "Estimated monthly payment",
      disclaimer: "Estimate only — actual rates, taxes, and insurance vary. HOA not included.",
      rowPi: "Principal & interest",
      rowTax: "Property tax",
      rowIns: "Home insurance",
      rowDown: "Down payment",
      rowLoan: "Loan amount",
      cta: "Get your exact rate",
      prefill:
        "I used your rate calculator: {price} home price, {down}% down, {rate}% rate, {term}-year term — estimated {monthly}/mo including taxes & insurance. I'd like exact rates for my situation.",
    },
    blog: {
      eyebrow: "Chapter 09 — Blog",
      h2a: "Notes from",
      h2b: "under our roof.",
      intro:
        "Market insight, loan tips, and Valley real estate know-how — straight from the team.",
      loadError: "The blog couldn't load right now. Please check back soon.",
    },
    contact: {
      eyebrow: "Chapter 10 — Contact",
      h2a: "Begin the",
      h2b: "conversation.",
      body: "Tell us where you are in your journey — buying, selling, or refinancing — and a member of our team will respond within one business day.",
      serviceArea: "Service Area —",
      area: "Servicing Valley Wide, Arizona",
      espanol: "Hablamos Español · We speak Spanish",
      name: "Full name",
      email: "Email address",
      phone: "Phone (optional)",
      message: "Tell us about your goals...",
      submit: "Send enquiry",
      sending: "Sending",
      toastOk: "Enquiry received — our team will reach out shortly.",
      toastErr: "Something went wrong. Please try again.",
      interests: {
        "Buying a home": "Buying a home",
        "Selling a home": "Selling a home",
        Refinancing: "Refinancing",
        "General enquiry": "General enquiry",
      },
      omarRole: "Mortgage & Real Estate",
      paulaRole: "Real Estate",
    },
    footer: {
      tagline:
        "Full-service real estate & mortgage brokerage. One team, every phase of the journey.",
      navigate: "Navigate",
      reachUs: "Reach Us",
      serviceArea: "Service Area",
      area: "Servicing Valley Wide, Arizona",
      rights: "All rights reserved.",
      tagline2: "Real Estate · Mortgage · One Roof",
    },
  },
  es: {
    nav: {
      whoWeAre: "Quiénes Somos",
      services: "Servicios",
      advantage: "Ventaja",
      reviews: "Reseñas",
      netSheet: "Neto Vendedor",
      blog: "Blog",
      team: "Equipo",
      calculator: "Calculadora",
      contact: "Contacto",
    },
    hero: {
      overline: "Opeam Group — Correduría de Bienes Raíces e Hipotecas",
      line1: "Bienes raíces.",
      line2: "Hipotecas.",
      line3: "Bajo un mismo techo.",
      subtext:
        "Un solo equipo maneja tu transacción y tu financiamiento — comprar, vender o refinanciar sin fricción entre tu agente y tu oficial de préstamos.",
      cta: "Iniciar una conversación",
      catch1: "Un Techo.",
      catch2: "Opciones Infinitas.",
      espanol: "Hablamos Español",
      scrollLabel: "Desplázate al contenido",
      alt: "Casa en el desierto bajo las montañas de Arizona al atardecer",
    },
    marquee: {
      items: [
        "Venta de Bienes Raíces",
        "Correduría Hipotecaria",
        "50+ Socios Prestamistas",
        "Un Equipo de Confianza",
        "Cierres Más Rápidos",
      ],
    },
    ch1: {
      eyebrow: "Capítulo 01 — Quiénes Somos",
      h2a: "Una correduría de servicio completo,",
      h2b: "bajo un mismo techo.",
      body: "Somos un equipo de correduría de bienes raíces e hipotecas de servicio completo. Al unir las transacciones inmobiliarias con el financiamiento hipotecario, ofrecemos una experiencia simplificada de principio a fin para clientes que compran, venden o refinancian su casa.",
      caption: "Fig. 01 — Cielo de Arizona, hora dorada",
      alt: "Cielo de Arizona — luz del sol a través de un cactus saguaro al atardecer",
    },
    ch2: {
      eyebrow: "Capítulo 02 — Servicios Principales",
      card1t: "Venta y Representación de Bienes Raíces",
      card1b: "Representación integral del vendedor, diseñada para maximizar el valor de tu casa, simplificar el proceso de listado y ejecutar ventas sin contratiempos.",
      card2t: "Servicios de Correduría Hipotecaria",
      card2b: "Acceso directo a productos de préstamos mayoristas competitivos, opciones de préstamo personalizadas y soluciones de financiamiento a la medida para compradores y propietarios actuales.",
    },
    ch3: {
      eyebrow: "Capítulo 03 — La Ventaja",
      label: "bancos mayoristas y prestamistas institucionales",
      body: "Como corredores hipotecarios independientes, mantenemos relaciones activas con más de cincuenta bancos mayoristas y prestamistas institucionales. En lugar de ofrecer productos rígidos de una sola institución, buscamos en todo el mercado para asegurar las mejores tasas de interés, plazos flexibles y estructuras de financiamiento personalizadas según tu perfil financiero.",
    },
    ch4: {
      eyebrow: "Capítulo 04 — Propuesta de Valor",
      h2a: "Sin fricción entre",
      h2b: "el agente y el oficial de préstamos.",
      row1t: "Comunicación más clara",
      row1b: "Una conversación, un equipo — nada se pierde entre tu agente y tu prestamista.",
      row2t: "Cierres más rápidos",
      row2b: "La transacción y el financiamiento avanzan en paralelo, no en secuencia.",
      row3t: "Un solo equipo de confianza",
      row3b: "Cada etapa de tu compra o venta de casa, manejada bajo un mismo techo.",
    },
    ch5: {
      eyebrow: "Capítulo 06 — Tu Equipo",
      h2a: "Las personas detrás de",
      h2b: "las llaves.",
      omarRole: "Hipotecas y Bienes Raíces",
      paulaRole: "Bienes Raíces",
    },
    testi: {
      eyebrow: "Capítulo 05 — Testimonios",
      h2a: "Palabras desde",
      h2b: "el Valle.",
      quotes: [
        {
          text: "Omar nos consiguió una tasa que nuestro propio banco no podía igualar, y Paula nos puso bajo contrato en nueve días. Un solo equipo realmente lo cambia todo.",
          name: "Sarah y Daniel M.",
          detail: "Compradores primerizos — Mesa",
        },
        {
          text: "Vender y comprar al mismo tiempo parecía imposible hasta que OPEAM manejó ambos lados. Respuestas claras, cierre rápido, cero drama.",
          name: "Rebecca T.",
          detail: "Vendedora y compradora — Gilbert",
        },
        {
          text: "Refinancié en español, en la mesa de mi cocina, sin presión. Me explicaron cada número hasta que me sentí cómodo firmando.",
          name: "José L.",
          detail: "Refinanciamiento — Phoenix",
        },
      ],
    },
    net: {
      eyebrow: "Capítulo 08 — Neto al Vender",
      h2a: "Conoce tu",
      h2b: "ganancia neta.",
      intro:
        "¿Vendiendo? Ajusta tus números — precio de venta, lo que aún debes, la comisión que eliges pagar y los costos estimados — y mira con cuánto podrías quedarte.",
      price: "Precio de venta",
      owe: "Saldo de hipoteca (lo que debes)",
      commission: "Comisión de agentes",
      closing: "Costos de cierre",
      title: "Cargos de título",
      repairs: "Reparaciones",
      resultTitle: "Neto estimado para ti",
      disclaimer:
        "Solo es un estimado — el saldo real, comisiones, impuestos y cargos varían. No incluye HOA ni prorrateos.",
      rowPrice: "Precio de venta",
      rowOwe: "Saldo de hipoteca",
      rowComm: "Comisión de agentes",
      rowClosing: "Costos de cierre",
      rowTitle: "Cargos de título",
      rowRepairs: "Reparaciones",
      cta: "Obtén tu hoja neta exacta",
      prefill:
        "Usé su calculadora de neto al vender: precio de venta {price}, saldo {owe}, comisión del {comm}%, costos de cierre {closing}, cargos de título {title}, reparaciones {repairs} — neto estimado {net}. Me gustaría una hoja neta exacta para mi casa.",
    },
    calc: {
      eyebrow: "Capítulo 07 — Calculadora de Pagos",
      h2a: "Conoce tu",
      h2b: "mensualidad.",
      intro:
        "Ajusta los números a tu situación y mira el panorama mensual completo — principal, interés, impuestos y seguro. Cuando estés listo, buscaremos tu tasa exacta entre más de 50 prestamistas.",
      price: "Precio de la casa",
      down: "Enganche",
      rate: "Tasa de interés",
      tax: "Impuesto predial (anual)",
      insurance: "Seguro de hogar (anual)",
      term: "Plazo del préstamo",
      yr: "años",
      resultTitle: "Pago mensual estimado",
      disclaimer:
        "Solo es un estimado — las tasas, impuestos y seguros reales varían. No incluye HOA.",
      rowPi: "Principal e interés",
      rowTax: "Impuesto predial",
      rowIns: "Seguro de hogar",
      rowDown: "Enganche",
      rowLoan: "Monto del préstamo",
      cta: "Obtén tu tasa exacta",
      prefill:
        "Usé su calculadora de tasas: casa de {price}, {down}% de enganche, tasa de {rate}%, plazo de {term} años — estimado de {monthly}/mes incluyendo impuestos y seguro. Me gustaría conocer las tasas exactas para mi situación.",
    },
    blog: {
      eyebrow: "Capítulo 09 — Blog",
      h2a: "Notas desde",
      h2b: "nuestro techo.",
      intro:
        "Análisis del mercado, consejos de préstamos y conocimiento inmobiliario del Valle — directo de nuestro equipo.",
      loadError: "El blog no pudo cargarse en este momento. Vuelve pronto.",
    },
    contact: {
      eyebrow: "Capítulo 10 — Contacto",
      h2a: "Inicia la",
      h2b: "conversación.",
      body: "Cuéntanos en qué punto estás — comprar, vender o refinanciar — y un miembro de nuestro equipo te responderá dentro de un día hábil.",
      serviceArea: "Área de Servicio —",
      area: "Todo el Valle, Arizona",
      espanol: "Hablamos Español · We speak Spanish",
      name: "Nombre completo",
      email: "Correo electrónico",
      phone: "Teléfono (opcional)",
      message: "Cuéntanos tus metas...",
      submit: "Enviar consulta",
      sending: "Enviando",
      toastOk: "Consulta recibida — nuestro equipo te contactará pronto.",
      toastErr: "Algo salió mal. Inténtalo de nuevo.",
      interests: {
        "Buying a home": "Comprar una casa",
        "Selling a home": "Vender una casa",
        Refinancing: "Refinanciamiento",
        "General enquiry": "Consulta general",
      },
      omarRole: "Hipotecas y Bienes Raíces",
      paulaRole: "Bienes Raíces",
    },
    footer: {
      tagline:
        "Correduría de bienes raíces e hipotecas de servicio completo. Un equipo, cada etapa del camino.",
      navigate: "Navegar",
      reachUs: "Contáctanos",
      serviceArea: "Área de Servicio",
      area: "Todo el Valle, Arizona",
      rights: "Todos los derechos reservados.",
      tagline2: "Bienes Raíces · Hipotecas · Un Solo Techo",
    },
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q === "es" || q === "en") return q;
    return localStorage.getItem("opeam-lang") || "en";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (l) => {
    localStorage.setItem("opeam-lang", l);
    setLangState(l);
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t: T[lang] }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
